'use client'

import { useEffect } from 'react'

export function MermaidRenderer() {
  useEffect(() => {
    let disposed = false

    async function renderDiagrams() {
      const nodes = Array.from(
        document.querySelectorAll<HTMLElement>('[data-type="diagram"]'),
      ).filter((node) => node.dataset['mermaidRendered'] !== 'true')
      if (nodes.length === 0) return

      const { default: mermaid } = await import('mermaid')
      if (disposed) return
      mermaid.initialize({
        startOnLoad: false,
        theme: 'neutral',
        securityLevel: 'strict',
        fontFamily: 'inherit',
        // A diagram that fails keeps its source on show; without this mermaid also left its own
        // error picture at the bottom of the page.
        suppressErrorRendering: true,
      })

      for (const node of nodes) {
        if (disposed) return
        if (node.dataset['mermaidRendered'] === 'true') continue
        node.dataset['mermaidRendered'] = 'true'

        const source =
          node.getAttribute('data-source') ||
          node.querySelector('.mermaid-source')?.textContent ||
          ''

        if (!source.trim()) continue

        try {
          const id = `mermaid-${crypto.randomUUID()}`
          const { svg } = await mermaid.render(id, source)

          const pre = node.querySelector<HTMLElement>('.mermaid-source')
          if (pre) pre.style.display = 'none'

          const svgContainer = document.createElement('div')
          svgContainer.innerHTML = svg
          const svgEl = svgContainer.querySelector('svg')
          if (svgEl) {
            svgEl.style.maxWidth = '100%'
            svgEl.style.height = 'auto'
            svgEl.style.margin = '0 auto'
            svgEl.style.display = 'block'
          }
          // Keep any figcaption below the rendered diagram
          const caption = node.querySelector('figcaption')
          if (caption) node.insertBefore(svgContainer, caption)
          else node.appendChild(svgContainer)
        } catch {
          // Leave the <pre> fallback visible
        }
      }
    }

    void renderDiagrams()

    // Lesson bodies are swapped client-side (EN/BN toggle) and React can
    // recreate the content subtree after hydration — both produce fresh,
    // unrendered diagram nodes. Re-scan on DOM changes; the data flag keeps
    // re-runs idempotent, so the observer goes quiet after rendering.
    const observer = new MutationObserver(() => {
      void renderDiagrams()
    })
    observer.observe(document.body, { childList: true, subtree: true })

    return () => {
      disposed = true
      observer.disconnect()
    }
  }, [])

  return null
}
